// universalPusher.js
'use strict';
import { LRUCache } from 'lru-cache';

import {
  bindEncryptedEvents,
  bindSubscriptionEvents,
} from './pusherController.js';
import { pusher as pusherDebug } from '../resource/debug.js';
import { addSecretKey } from './pusherKeychain.js';
import { STREAM_RELATED_EVENTS } from '../resource/pusherUtils.js';
import {
  getPusherAuthHeaders,
  getPusherConfig,
  getPusherSubscribeRecord,
  getPusherTargets,
} from '../resource/pusherTargetStore.js';
import fetch from '../resource/customFetch.js';

const debugLog = pusherDebug.extend('log');
const debugErrorLog = pusherDebug.extend('error');

const canUnsubscribeChannelPrefixes = ['private-enc-user@'];
const channelEventBinders = [
  {
    regex: new RegExp('^private-enc-user@([0-9a-z]{24})$'),
    binder: ({ matchResult: [, userId] = [], channel }) => {
      bindEncryptedEvents(channel, event =>
        STREAM_RELATED_EVENTS.includes(event)
          ? { userId, streamId: userId }
          : { userId }
      );
    },
  },
  {
    regex: new RegExp('^private-enc-stream@([0-9a-z]{24})$'),
    binder: ({ matchResult: [, streamId] = [], channel }) => {
      bindEncryptedEvents(channel, { streamId });
    },
  },
];
const subscribeChannel = ({ channelName, pusherInstance }) => {
  const log = debugLog.extend('subscribeChannel');
  const errorLog = debugErrorLog.extend('subscribeChannel');
  log('start', { channelName, pusherInstance });

  if (!pusherInstance) {
    errorLog('missing pusherInstance', { channelName, pusherInstance });
    throw new Error('missing pusherInstance');
  }

  let matchResult = null;
  const binder = channelEventBinders.find(binder => {
    return !!(matchResult = channelName.match(binder.regex));
  })?.binder;
  if (binder) {
    log('subscribing', { channelName, pusherInstance });
    const channel = pusherInstance.subscribe(channelName);
    bindSubscriptionEvents(channel);
    binder?.({ matchResult, channel });
  } else {
    errorLog('missing binder', { channelName });
  }
};

/**
 * Resolve the tab-local subscription targets into the shape the diffing
 * helpers below expect. Each target is derived into a *fresh* object because
 * the store owns `subscriptionMap` and must survive this tick untouched.
 * @returns {Object<string, object>}
 */
const getResolvedPusherTargets = () => {
  const targets = getPusherTargets();
  return Object.keys(targets).reduce((accumulated, channelName) => {
    const target = targets[channelName];
    const subscriptionMap = target.subscriptionMap || {};
    accumulated[channelName] = {
      channelName,
      isCritical: target.isCritical,
      shouldSubscribe: Object.keys(subscriptionMap).some(
        ref => subscriptionMap[ref]
      ),
      timestamp: target.timestamp,
      expiryTimestamp: target.expiryTimestamp,
    };
    return accumulated;
  }, {});
};

/**
 * Process pusher subscription target channels of this tab.
 * It will process targets with these steps:
 *   * Resolve this tab's targets (getResolvedPusherTargets())
 *   * Compare targets with pusherInstance to compute channel differences (getChannelDiffs())
 *   * Unsubscribe extra channels in pusherInstance
 *   * Get stale channels, which means channels need to re-subscribe (getStaleChannels())
 *   * Compute next batch channels from missing channels, stale channels and
 *     subscribe records for backoff (getNextBatchChannels())
 *   * Fetch authorize data for next batch channels and store in buffer (subscribeChannel)
 * @param {Object} {pusherInstance} - using pusher instance
 */
export const processPusherTargets = async function ({ pusherInstance }) {
  const log = debugLog.extend('processPusherTargets');
  const errorLog = debugErrorLog.extend('processPusherTargets');
  log('start', { pusherInstance });

  if (!pusherInstance) {
    errorLog('missing pusherInstance', { pusherInstance });
    throw new Error('missing pusherInstance');
  }

  const record = getPusherSubscribeRecord();
  const pusherTargets = getResolvedPusherTargets();
  log('getResolvedPusherTargets()', { pusherTargets });

  const { extras: extraChannels, missings: missingChannels } = getChannelDiffs({
    targets: pusherTargets,
    pusherInstance,
  });
  log('getChannelDiffs()', {
    extraChannels,
    missingChannels,
    pusherTargets,
  });

  // unsubscribe extra channels
  extraChannels
    .filter(channel =>
      canUnsubscribeChannelPrefixes.some(prefix =>
        channel.name.startsWith(prefix)
      )
    )
    .forEach(targetChannel => {
      const channel = pusherInstance.channels.channels[targetChannel.name];
      channel?.unsubscribe();
      channel?.disconnect();
      delete pusherInstance.channels.channels[targetChannel.name];
    });

  const staleChannels = getStaleChannels({
    targets: pusherTargets,
    record,
    pusherInstance,
  });
  log('getStaleChannels()', {
    staleChannels,
    pusherTargets,
    record,
    pusherInstance,
  });

  // handle missing and stale channels
  const now = Date.now();
  const nextBatchChannels = getNextBatchChannels({
    record,
    missingChannels,
    staleChannels,
    now,
  });
  log('getNextBatchChannels()', {
    nextBatchChannels,
    missingChannels,
    record: JSON.parse(JSON.stringify(record)),
    now,
  });

  // increase retriedCount and raise subscribing flags
  nextBatchChannels.forEach(channel => {
    Object.assign(record, {
      [channel.channelName]: {
        ...record[channel.channelName],
        retriedCount: channel.retriedCount + 1,
        isSubscribing: true,
      },
    });
  });

  // fetch auth data, we fetch auth by ourself to utlize better retry control
  const {
    authEndpoint: endpoint,
    appId,
    language,
    headers: initialHeaders,
  } = getPusherConfig() || {};
  // the stored config only holds the headers captured when pusher was
  // initialized, so prefer the headers refreshed on every token / identity
  // change (see action/updatePusherDistinctId.js)
  const latestHeaders = getPusherAuthHeaders();
  const headers = Object.keys(latestHeaders || {}).length
    ? latestHeaders
    : initialHeaders;
  const fetchBatchAuthenticateParam = {
    channels: nextBatchChannels,
    endpoint,
    appId,
    socketId: pusherInstance.connection.socket_id,
    language,
    headers,
  };
  const channelsData = await fetchBatchAuthenticate(
    fetchBatchAuthenticateParam
  );
  log('fetchBatchAuthenticate()', {
    channelsData,
    nextBatchChannels,
    fetchBatchAuthenticateParam,
  });

  // use fetched data to subscribe channels
  Object.keys(channelsData).forEach(channelName => {
    // save fetchedTimestamp for expiry check
    if (!record[channelName]) {
      // not sure why sometimes the record is missing
      record[channelName] = { retriedCount: 1 };
    }
    record[channelName].fetchedTimestamp = Date.now();

    if (!pusherInstance.channels.channels[channelName]?.subscribed) {
      // only subscribe missing channels
      subscribeChannel({ channelName, pusherInstance });
      log('subscribe missing channel', {
        pusherInstance,
        channelName,
        data: channelsData[channelName],
      });
    } else {
      // send auth channel data to main thread
      pusherInstance.onAuth?.({
        channel: channelName,
        data: channelsData[channelName],
      });
      log('send auth channel data for stale channel', {
        pusherInstance,
        channelName,
        data: channelsData[channelName],
      });
    }
  });

  // reset subscribing flags
  nextBatchChannels.forEach(channel => {
    if (record[channel.channelName]) {
      record[channel.channelName].isSubscribing = false;
    }
  });
};

const getChannelDiffs = ({ targets = {}, pusherInstance }) => {
  const log = debugLog.extend('getChannelDiffs');
  const errorLog = debugErrorLog.extend('getChannelDiffs');

  if (!pusherInstance) {
    const error = new Error('missing pusherInstance');
    errorLog(error);
    throw error;
  }

  const current = pusherInstance.channels.channels;
  log('start', { targets, current });

  const combined = [
    ...new Set([...Object.keys(current), ...Object.keys(targets)]),
  ];
  const diffs = combined.reduce((acc, channelName) => {
    const shouldSubscribe = !!targets[channelName]?.shouldSubscribe;
    const isSubscribed = !!current[channelName]?.subscribed;
    if (isSubscribed !== shouldSubscribe) {
      acc[channelName] = shouldSubscribe
        ? targets[channelName]
        : { channelName, ...current[channelName], shouldSubscribe };
    }
    return acc;
  }, {});
  log('diffs', { diffs, combined });

  const extras = Object.keys(diffs)
    .filter(channelName => !diffs[channelName].shouldSubscribe)
    .map(channelName => diffs[channelName]);
  const missings = Object.keys(diffs)
    .filter(channelName => diffs[channelName].shouldSubscribe)
    .map(channelName => diffs[channelName]);
  log('result', { extras, missings });

  return { extras, missings };
};

const getStaleChannels = ({ targets = {}, record = {}, pusherInstance }) => {
  const log = debugLog.extend('getStaleChannels');
  const errorLog = debugErrorLog.extend('getChannelDiffs');

  if (!pusherInstance) {
    const error = new Error('missing pusherInstance');
    errorLog(error);
    throw error;
  }

  const current = pusherInstance.channels.channels;
  log('start', { targets, record, current });

  const subscribedChannelNames = Object.keys(current).filter(
    channelName => current[channelName].subscribed
  );
  const staleChannelNames = subscribedChannelNames.filter(channelName => {
    return (
      targets[channelName]?.shouldSubscribe &&
      targets[channelName].expiryTimestamp >
        record[channelName]?.fetchedTimestamp
    );
  });

  return staleChannelNames.map(channelName => targets[channelName]);
};

const FibonacciSequence = [1, 1, 2, 3, 5, 8, 13, 21, 34];
const getNextBatchChannels = ({
  record = {},
  missingChannels = [],
  staleChannels = [],
  now = Date.now(),
  batchSize = 10,
  criticalRetryUnitMsec = 1000, // TODO: remote config
  nonCriticalRetryUnitMsec = 5000, // TODO: remote config
}) => {
  const log = debugLog.extend('getNextBatchChannels');
  log('start', {
    record,
    missingChannels,
    now,
    batchSize,
    criticalRetryUnitMsec,
    nonCriticalRetryUnitMsec,
  });

  // compute next retry time for every channel
  const channelDataArray = missingChannels
    .filter(channel => !record[channel.channelName]?.isSubscribing)
    .map(channel => {
      const timestamp = channel.timestamp;
      const isCritical = channel.isCritical;
      const retriedCount = record[channel.channelName]?.retriedCount || 0;
      const retryUnit = isCritical
        ? criticalRetryUnitMsec
        : nonCriticalRetryUnitMsec;
      const nextRetryOffsetMsec = new Array(retriedCount)
        .fill()
        .map((_, index) => {
          return (
            retryUnit *
            (FibonacciSequence[index] ||
              FibonacciSequence[FibonacciSequence.length - 1])
          );
        })
        .reduce((acc, cur) => acc + cur, 0);
      const nextRetryTimestamp = timestamp + nextRetryOffsetMsec;
      return {
        ...channel,
        timestamp,
        retriedCount,
        isCritical,
        nextRetryTimestamp,
        nextRetryOffsetMsec,
      };
    });

  // filter channels which retry time past
  const sortedCriticalMissingChannelDataArray = channelDataArray
    .filter(channel => channel.isCritical)
    .filter(channel => channel.nextRetryTimestamp <= now)
    .sort((a, b) => a.nextRetryTimestamp - b.nextRetryTimestamp)
    .slice(0, batchSize);

  const sortedNoncriticalMissingChannelDataArray = channelDataArray
    .filter(channel => !channel.isCritical)
    .filter(channel => channel.nextRetryTimestamp <= now)
    .sort((a, b) => a.nextRetryTimestamp - b.nextRetryTimestamp)
    .slice(0, batchSize);

  const sortedCriticalStaleChannelDataArray = staleChannels
    .filter(channel => channel.isCritical)
    .sort((a, b) => a.expiryTimestamp - b.expiryTimestamp)
    .slice(0, batchSize);

  const sortedNonCriticalStaleChannelDataArray = staleChannels
    .filter(channel => !channel.isCritical)
    .sort((a, b) => a.expiryTimestamp - b.expiryTimestamp)
    .slice(0, batchSize);

  const batchedChannelDataArray = [
    ...sortedCriticalStaleChannelDataArray,
    ...sortedCriticalMissingChannelDataArray,
    ...sortedNonCriticalStaleChannelDataArray,
    ...sortedNoncriticalMissingChannelDataArray,
  ].slice(0, batchSize);

  // fill batch to batchSize with channels not meet retry time yet
  if (
    batchedChannelDataArray.length &&
    batchedChannelDataArray.length < batchSize
  ) {
    const stuffingChannelDataArray = channelDataArray
      .filter(channel => channel.nextRetryTimestamp > now)
      .sort((a, b) => a.nextRetryTimestamp - b.nextRetryTimestamp)
      .sort((a, b) => !!b.isCritical - !!a.isCritical)
      .slice(0, batchSize - batchedChannelDataArray.length);
    batchedChannelDataArray.push(...stuffingChannelDataArray);
  }

  log('result', { batchedChannelDataArray });
  return batchedChannelDataArray;
};

const authorizeBuffer = new LRUCache({ max: 64 });
const fetchBatchAuthenticate = async ({
  channels = [],
  endpoint,
  appId,
  socketId,
  language,
  headers = {},
}) => {
  const log = debugLog.extend('fetchBatchAuthenticate');
  const errorLog = debugErrorLog.extend('fetchBatchAuthenticate');

  log('start', { channels, endpoint, appId, socketId, language, headers });

  if (!channels?.length || !endpoint || !appId || !socketId) {
    errorLog('missing params', { channels, endpoint, appId, socketId });
    return [];
  }

  const url = new URL(endpoint);
  url.searchParams.set('app_id', appId);
  url.searchParams.set('socket_id', socketId);
  if (language) {
    url.searchParams.set('lang', language);
  }

  channels
    .map(channel => channel.channelName)
    .sort()
    .forEach(channel => {
      url.searchParams.append('channels', channel);
    });

  const options = {
    headers: {
      ...headers,
    },
  };

  log('fetch params', { href: url.href, options });
  try {
    const response = await fetch(url.href, options);
    const payload = await response.json();
    Object.keys(payload).forEach(channelName => {
      authorizeBuffer.set(`${socketId}-${channelName}`, payload[channelName]);
    });
    return payload;
  } catch (error) {
    errorLog('fetch error', { error });
    return {};
  }
};

/**
 * Use fetched pusher authorize data to do callback authorize
 * @param {string} {channelName} - pusher channel name
 * @param {string} {socketId} - pusher socket id
 * @param {Object} {[options={}]} - options
 * @param {function} {callback} - authorize function, arg1: false||authError, arg2: authData
 */
export const bufferedAuthorizer = ({
  channelName,
  socketId,
  options = {},
  callback,
}) => {
  const log = debugLog.extend('bufferedAuthorizer');
  const errorLog = debugErrorLog.extend('bufferedAuthorizer');
  log('start', { channelName, socketId, options });
  const cacheKey = `${socketId}-${channelName}`;
  const auth = authorizeBuffer.get(cacheKey);
  if (auth) {
    log('auth hit', { channelName, socketId, auth });
    addSecretKey({ channelName, secretKey: auth.shared_secret });
    callback(false, auth);
  } else {
    errorLog('missing auth', { channelName, socketId, options });
    callback(new Error('missing auth'));
  }
};
